import { ComponentType, ForwardRefExoticComponent, ReactNode, RefAttributes } from 'react';
import { GroupBase, InputActionMeta, InputProps, MultiValue, MultiValueProps } from 'react-select';
import { ButtonNoVariantProps } from 'ui/button/types';
import { TooltipProps } from 'ui/tooltip/types';

/**
 * This type is not currently properly exported from react-select
 * @see https://github.com/JedWatson/react-select/issues/5535
 */
export interface FilterOptionOption<Option> {
  readonly label: string;
  readonly value: string;
  readonly data: Option;
}

export type ErrorMessageProps = {
  children: ReactNode;
};

export type SelectValueType = string | number | boolean;
export enum CreateOptionPosition {
  first = 'first',
  last = 'last',
}

export interface BaseOption<TValue extends SelectValueType = string> {
  __isNew__?: boolean;
  categoryId?: string;
  label: ReactNode; // what get shows in the option list and the display
  value: TValue; // the value is what gets stored in a DB record somewhere
  Suffix?: ReactNode | string; // an optional rendered item after the label
  selectAllCategory?: string; // used for the select all values, returns the given category
  allCategoryOptionsSelected?: boolean; // boolean to determine if all values within a category are selected
  tooltipContent?: TooltipProps['tooltipContent'];
  isDisabled?: boolean; // determines if the option is disabled
  description?: ReactNode; // secondary description for an option that's longer than the Suffix
}

export type SelectionDetails<TValue extends SelectValueType = string> = {
  selectedOptions: BaseOption<TValue>[];
};

export type OnCreateHandler = (inputValue: string, actionMeta?: InputActionMeta) => void;
export type OnSelectHandler = (val: BaseOption[]) => void;
export type FormatCreateLabel = (inputValue: string) => ReactNode;

export type SelectProps = {
  /**
   * Whether or not the select should be focused on mount
   */
  autoFocus?: boolean;
  /**
   * Remove the currently focused option when the user presses backspace when Select isClearable or isMulti
   */
  backspaceRemovesValue?: boolean;
  /**
   * Whether or not the select should be able to create new options
   */
  canCreate?: boolean;
  /**
   * A map of `categoryId` to category labels. If this is provided, the options will be grouped by category.
   */
  categoryLabels?: Record<string, string | number>;
  /** whether to show a category separator between category groups */
  showCategoryDivider?: boolean;
  /**
   * The cutoff number of options before the select switches to a compact view
   */
  compactCutoff?: number;
  /**
   * Whether the selected values should be displayed in the control.
   */
  shouldHideDisplayValues?: boolean;
  /**
   * Preffix text displayed when creating an option
   */
  createPrefix?: string;
  /**
   * Suffix text displayed when creating an option
   */
  createSuffix?: string;
  /**
   * Custom styles for the select
   * @see https://react-select.com/styles
   */
  customTheme?: (errorMsg?: ReactNode) => Record<any, any>;
  /**
   * Error message to display
   */
  errorMsg?: string;
  /**
   * The id of the select
   */
  id?: string;
  /**
   * The id of the search input
   */
  inputId?: string;
  /**
   * Whether or not the select is clearable
   */
  isClearable?: boolean;
  /**
   * Whether or not the select is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether or not the select is in a loading state
   */
  isLoading?: boolean;
  /**
   * Whether or not the select should support multiple selections
   */
  isMulti?: boolean;
  /**
   * Whether or not to use a portal for the select options dropdown
   */
  usePortal?: boolean;
  /**
   * Whether or not to use virtualized options
   * @note this does not work with multiple categories yet.
   */
  isVirtualized?: boolean;
  /**
   * Callback triggered when a new option is created
   */
  onCreateOption?: OnCreateHandler;
  /**
   * Callback triggered when an option is selected
   */
  onSelect: OnSelectHandler;
  /**
   * The maximum height of the menu before scrolling
   */
  maxMenuHeight?: number;
  /**
   * The target element to append the menu portal to
   */
  menuPortalTarget?: HTMLElement | null;
  /**
   * Used as the limit number of options that can be selected
   */
  optionLimit?: number;
  /**
   * List of options to display in the select
   */
  options: BaseOption[];
  /**
   * Placeholder text for the select
   */
  placeholder?: string;
  /** Current value of the select */
  value?: MultiValue<any>;
  /** Used to set the selected value on the initial render */
  defaultValue?: MultiValue<any>;
  /**
   * Function to determine whether an option should be returned for a given search value
   */
  filterOption?: (
    option: FilterOptionOption<BaseOption<string> | NewBaseOption>,
    inputValue: string,
  ) => boolean;
  /**
   * Function to determine if an option is disabled
   */
  isOptionDisabled?: (option: BaseOption) => boolean;
  /**
   * Toggle state for managing the menu/dropdown open state
   */
  isMenuOpen?: boolean;
  /**
   * Callback triggered when menu is open
   */
  onMenuOpen?: () => void;
  /**
   * Callback triggered when menu is closed
   */
  onMenuClose?: () => void;
  /**
   * Default state of the menu/dropdown when the select is rendered
   */
  isDefaultMenuOpen?: boolean;
  /**
   * Whether or not to hide selected options from the dropdown/menu
   */
  hideSelectedOptions?: boolean;
  /**
   * Whether or not to hide the dropdown indicator
   */
  hideDropdownIndicator?: boolean;
  /**
   * The value of the search input
   */
  inputValue?: string;
  /**
   * Default placement of the menu in relation to the control.
   * 'auto' will flip when there isn't enough space below the control.
   * https://react-select.com/props#select-props
   */
  menuPlacement?: 'auto' | 'bottom' | 'top';
  /**
   * Callback triggered when a user type in the input
   */
  onInputChange?: OnCreateHandler;
  /**
   * Action that fires when user scrolls to bottom of the menu
   */
  onMenuScrollToBottom?: (e?: TouchEvent | WheelEvent) => void;
  /**
   * Custom component for rendering a multi-select value
   */
  MultiValue?: ComponentType<
    MultiValueProps<BaseOption<string>, boolean, GroupBase<BaseOption<string>>>
  >;
  /**
   * Custom component for rendering the select input
   */
  Input?: ComponentType<InputProps<BaseOption<string>, boolean, GroupBase<BaseOption<string>>>>;
  /**
   * Override default behavior of whether the menu should close when an option is selected
   */
  closeMenuOnSelect?: boolean;
};

export type SelectPopoutProps = {
  options: BaseOption[];
  onSelect: OnSelectHandler;
  categoryLabels?: Record<string, string | number>; // used to break up the select into categories
  value: MultiValue<any>;
  optionLimit?: number; // used as the limit number of options that can be selected
  inputId?: string; // used for labels
  id?: string; // id for select
  customTheme?: Record<any, any>;
  Trigger?: ForwardRefExoticComponent<
    Omit<ButtonNoVariantProps, 'ref'> & RefAttributes<HTMLButtonElement>
  >;
};

export type MultiSelectProps = Omit<SelectProps, 'isMulti'> & {
  canBulkPaste?: boolean; // determines if bulk paste a long string by delimiter
  pasteDelimiter?: string | RegExp; // string character or regex exp to split text with
};

export type CreatableSelectProps = {
  formatCreateLabel?: FormatCreateLabel;
  createOptionPosition?: CreateOptionPosition;
  onCreateOption?: OnCreateHandler;
};

export interface NestedBaseOption {
  options: BaseOption[];
}

export interface NewBaseOption extends BaseOption {
  __isNew__: boolean;
}
